/**
 * DataTable header-row cell rendering, shared between the real thead and its "frozen"
 * copy (see `stickyHeaderOffsetPx` in data-table.tsx). Split out of data-table.tsx to
 * keep that file within the max-lines-per-function lint budget — mirrors the
 * data-table-rows.tsx precedent for row rendering.
 */
import * as React from 'react';
import { resolveColResizable, resolveSortClickHandler } from './data-table-hooks.js';
import { SelectionHeaderCell, ColumnHeaderCell, RowLinkHeaderCell } from './data-table-helpers.js';
import type { ColumnDef } from './data-table.js';

interface SortOrder {
  key: string;
  order: number;
  dir: 'asc' | 'desc';
}

export interface HeaderRowContext<T> {
  columns: ColumnDef<T>[];
  hasRowLink: boolean;
  hasRowSelection: boolean;
  headerCheckState: boolean | 'indeterminate';
  handleHeaderToggle: () => void;
  isResizing: boolean;
  draggedColumnId: string | null;
  dragOverColumnId: string | null;
  resizableColumns: boolean;
  reorderEnabled: boolean;
  onColumnDragStart: (colId: string) => void;
  onColumnDragOver: (colId: string, e: React.DragEvent) => void;
  onColumnDrop: (colId: string) => void;
  onColumnDragEnd: () => void;
  onColumnResizeStart: (e: React.MouseEvent, colId: string) => void;
  onSort?: (sortKey: string, shiftKey: boolean) => void;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  sortOrders?: SortOrder[];
}

/**
 * Renders one header row's cells (row-link/selection structural cells + every column).
 * Called twice when `stickyHeaderOffsetPx` is set: once for the frozen, visible,
 * interactive copy (`measurementOnly: false`), once for the real, invisible, in-flow
 * thead that exists purely to give the browser's own table-layout algorithm the same
 * content to size columns from (`measurementOnly: true`) — see `ColumnHeaderCell`'s own
 * `measurementOnly` doc for why that invisible copy suppresses test ids/interactivity.
 */
export function renderHeaderRowCells<T>(
  ctx: HeaderRowContext<T>,
  widthResolver: (colId: string, colWidth?: number) => number | undefined,
  measurementOnly: boolean,
): React.ReactNode {
  return (
    <>
      {ctx.hasRowLink ? <RowLinkHeaderCell measurementOnly={measurementOnly} /> : null}
      {ctx.hasRowSelection ? (
        <SelectionHeaderCell
          checkState={ctx.headerCheckState}
          onToggle={ctx.handleHeaderToggle}
          measurementOnly={measurementOnly}
        />
      ) : null}
      {ctx.columns.map((col) => (
        <ColumnHeaderCell
          key={col.id}
          col={col}
          width={widthResolver(col.id, col.width)}
          isResizing={ctx.isResizing}
          isDragged={ctx.draggedColumnId === col.id}
          isDraggedOver={ctx.dragOverColumnId === col.id && ctx.draggedColumnId !== col.id}
          reorderEnabled={ctx.reorderEnabled}
          showResizeHandle={!col.fixed && resolveColResizable(col.resizable, ctx.resizableColumns)}
          onDragStart={() => ctx.onColumnDragStart(col.id)}
          onDragOver={(e) => ctx.onColumnDragOver(col.id, e)}
          onDrop={() => ctx.onColumnDrop(col.id)}
          onDragEnd={ctx.onColumnDragEnd}
          onResizeStart={(e) => ctx.onColumnResizeStart(e, col.id)}
          onSortClick={resolveSortClickHandler(col, ctx.onSort, ctx.sortOrders)}
          sortBy={ctx.sortBy}
          sortDir={ctx.sortDir}
          sortOrders={ctx.sortOrders}
          measurementOnly={measurementOnly}
        />
      ))}
    </>
  );
}
